import React, { useState, useEffect, useCallback } from 'react';
// Note: useLocation is needed by MobileMenu and ScrollToTop
// useNavigate is no longer needed in this component
import { Link, useLocation } from 'react-router-dom';
// The required local logo path.
import Logo from './Logo.png'; 

// --- SVG Icon Components (Unchanged) ---
const XIcon = ({ size = 28, strokeWidth = 2.5, color = 'currentColor', className = '' }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke={color} 
        strokeWidth={strokeWidth} 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={className} 
    >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

const MenuIcon = ({ size = 28, strokeWidth = 2.5, color = 'currentColor' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
);

// --- NAVIGATION CONFIGURATION (UPDATED) ---
const navLinks = [
    { to: "/", label: "HOME" },
    { to: "/about", label: "ABOUT" },
    { to: "/services", label: "SERVICES" },
    { to: "/projects", label: "PROJECTS" },
    { to: "/ourworks", label: "OUR WORKS" }, // Added "Our Works" link
    { to: "/materials", label: "MATERIALS" },
    { to: "/contact", label: "CONTACT" },
];

// --- Custom Styles Component (Unchanged) ---
const CustomStyles = () => (
    <style>{`
        /* Define Brand Colors */
        :root {
            --brand-red: #DC2626; /* Tailwind Red 600 */
            --brand-dark: #1F2937; /* Tailwind Gray 800 */
            --brand-light: #F9FAFB; /* Tailwind Gray 50 */
            --navbar-height: 75px;
        }

        .logo-gradient-text {
            background: linear-gradient(to right, var(--brand-red), var(--brand-dark));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        .btn-brand-red {
            padding: 3px 8px; 
            font-size: 0.75rem; /* text-xs */
            border-radius: 9999px; 
            font-weight: 600;
            color: white;
            background-color: var(--brand-red);
            transition: all 0.3s ease;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
        }
        
        @media (min-width: 1024px) {
            .btn-brand-red {
                padding: 8px 16px; 
                font-size: 0.875rem; /* text-sm */
            }
        }

        .btn-brand-red:hover {
            background-color: #EF4444; 
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
        }
        
        .desktop-nav-link {
            position: relative;
            color: var(--brand-dark);
            transition: color 0.3s ease;
        }
        .desktop-nav-link::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -5px;
            left: 50%;
            transform: translateX(-50%);
            background-color: var(--brand-red);
            transition: width 0.3s ease, background-color 0.3s ease;
        }
        .desktop-nav-link:hover {
            color: var(--brand-red); 
        }
        .desktop-nav-link:hover::after {
            width: 100%;
            background-color: #EF4444; 
        }
        .desktop-nav-link.active {
            font-weight: 600;
            color: var(--brand-red);
        }
        .desktop-nav-link.active::after {
            width: 100%;
            background-color: var(--brand-red);
        }

        .mobile-nav-link-item {
            padding: 6px 12px; 
            margin: 2px 0; 
            border-radius: 8px;
            display: block;
            transition: background-color 0.3s ease, color 0.3s ease;
        }
        .mobile-nav-link-item:hover {
            background-color: #FEF2F2; 
            color: var(--brand-dark); 
        }
        .mobile-nav-link-item.active {
            font-weight: 700;
            background-color: #FEE2E2; 
            color: var(--brand-red);
        }

        .mobile-contact-link {
            position: relative;
            display: inline-block;
            transition: color 0.3s ease, transform 0.3s ease;
        }
        .mobile-contact-link:hover {
            color: var(--brand-red);
            transform: translateX(5px);
        }

        .mobile-menu-bg {
            background-color: white; 
        }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInUp { from { transform: translateY(50px) scale(0.95); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
        .modal-enter { animation: fadeIn 0.3s forwards; }
        .modal-content-enter { animation: slideInUp 0.4s forwards cubic-bezier(0.25, 0.46, 0.45, 0.94); }
    `}</style>
);


// --- MobileMenu component ---
const MobileMenu = ({ isOpen, closeMenu, location }) => {
    const menuClasses = `fixed top-0 right-0 w-full max-w-sm md:max-w-md z-[60] flex flex-col h-full overflow-y-auto p-6 md:p-8 
        transition-transform duration-500 ease-out mobile-menu-bg shadow-2xl 
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`;

    return (
        <>
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
                    onClick={closeMenu}
                ></div>
            )}
            <div className={menuClasses}>
                <div className="flex justify-end pt-2 pb-2"> 
                    <button 
                        onClick={closeMenu} 
                        className="p-2 rounded-full relative group transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-red overflow-hidden bg-white z-50" 
                        aria-label="Close menu"
                    >
                        <span className="absolute inset-0 rounded-full bg-red-50 scale-100 opacity-100 transition-all duration-300 group-hover:scale-150 group-hover:opacity-0"></span>
                        <XIcon 
                            color="var(--brand-red)" 
                            className="relative z-10 transition-all duration-300 group-hover:scale-125 group-hover:rotate-90" 
                        />
                    </button>
                </div>

                <nav className="flex flex-col justify-start items-center text-center flex-grow py-4 pt-2"> 
                    <div className="space-y-0.5 w-full max-w-xs mx-auto"> 
                        {navLinks.map((link, index) => (
                            <Link 
                                key={link.to}
                                to={link.to} 
                                onClick={closeMenu} 
                                className={`mobile-nav-link-item text-xl font-medium tracking-widest text-brand-dark transition-all duration-700 ease-in-out transform relative 
                                    ${location.pathname === link.to ? 'active' : ''}
                                    ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                                `}
                                style={{ transitionDelay: `${isOpen ? index * 100 : 0}ms` }}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    <div 
                        className={`space-y-1 mt-4 mb-2 transition-all duration-700 ease-in-out transform text-brand-dark
                            ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                        `}
                        style={{ transitionDelay: `${isOpen ? navLinks.length * 100 : 0}ms` }}
                    > 
                        <h3 className="text-xl font-bold text-brand-red border-b border-red-300 pb-1 mb-1">GET IN TOUCH</h3>
                        
                        <div className="text-sm"> 
                            <p className="font-semibold text-base">CALL US</p>
                            <a href="tel:+919962310466" className="mobile-contact-link block text-base">+91 9962310466</a>
                            <a href="tel:+919710366466" className="mobile-contact-link block text-lg text-brand-red font-bold">+91 9710366466</a>
                        </div>
                        
                        <div className="pt-2 text-sm"> 
                            <p className="font-semibold text-base">EMAIL US</p>
                            <a href="mailto:interiorsdred@gmail.com" className="mobile-contact-link block text-base">interiorsdred@gmail.com</a>
                        </div>
                    </div>
                </nav>
            </div>
        </>
    );
};

// --- ContactModal Component ---
const ContactModal = ({ isOpen, onClose }) => {
    const phoneNumber = '9962310466';
    const customWhatsAppMessage = "Hello, I'm ready to discuss my interior project and would like to know more about your design process!";

    const handleCall = () => {
        window.location.href = `tel:${phoneNumber}`;
        onClose();
    };

    const handleWhatsApp = () => {
        const encodedMessage = encodeURIComponent(customWhatsAppMessage);
        window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-white bg-opacity-70 modal-enter"
            onClick={onClose}
        >
            <div 
                className="bg-white border border-red-400/30 rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center relative modal-content-enter text-gray-900"
                onClick={e => e.stopPropagation()}
            >
                <h3 className="text-3xl font-extrabold text-gray-900 mb-6">How would you like to connect?</h3>
                <div className="space-y-4">
                    <button
                        onClick={handleCall}
                        className="w-full py-4 px-6 bg-white text-red-600 font-bold rounded-xl shadow-lg border-2 border-red-600 hover:bg-red-50 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl"
                    >
                        <span className="mr-2">📞</span> Call Us
                    </button>
                    <button
                        onClick={handleWhatsApp}
                        className="w-full py-4 px-6 bg-gray-900 text-white font-bold rounded-xl shadow-lg border-2 border-red-600 hover:bg-gray-700 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl"
                    >
                        <span className="mr-2">💬</span> WhatsApp Us
                    </button>
                </div>
                <button
                    onClick={onClose}
                    className="mt-8 text-gray-600 hover:text-gray-900 transition-colors duration-300 text-sm"
                >
                    No, thanks
                </button>
            </div>
        </div>
    );
};

// --- ScrollToTop Component ---
const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth' 
        });
    }, [pathname]);

    return null; 
};

// --- Main DesignNavbar Component ---
const DesignNavbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false); 
    const location = useLocation();

    const closeMenu = useCallback(() => setIsMenuOpen(false), []);
    const openModal = useCallback(() => setIsModalOpen(true), []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    useEffect(() => {
        const fontLink = document.createElement('link');
        fontLink.rel = 'stylesheet';
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap';
        document.head.appendChild(fontLink);

        return () => {
            document.head.removeChild(fontLink);
        };
    }, []);

    useEffect(() => {
        if (isMenuOpen || isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen, isModalOpen]);

    return (
        <>
            <CustomStyles />
            <ScrollToTop /> 
            
            <header className="fixed top-0 left-0 w-full z-50 bg-white shadow-xl h-[var(--navbar-height)]">
                <div className="container mx-auto pl-2 pr-6 h-full flex items-center justify-between relative"> 
                    
                    {/* LEFT GROUP */}
                    <div className="flex items-center lg:space-x-8 flex-shrink-0"> 
                        <Link 
                            to="/" 
                            className="z-20 flex-shrink-0 flex items-center space-x-1" 
                        >
                            <img 
                                src={Logo} 
                                alt="D Red Interiors Logo"                                                                className="h-20 md:h-24 w-auto py-1 md:py-3 flex-shrink-0"                                                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallbackText = e.currentTarget.nextSibling;
                                    if(fallbackText) fallbackText.style.display = 'block';
                                }}
                            />
                            <div 
                                style={{display: 'none'}} 
                                className="text-sm font-bold whitespace-nowrap lg:hidden" 
                            > 
                                <span style={{ color: 'var(--brand-dark)'}}>'D'</span>
                                <span style={{ color: 'var(--brand-red)'}}>Red </span>
                                <span style={{ color: 'var(--brand-dark)'}}>Logo</span>
                            </div>
                        </Link>
                        
                        {/* Desktop Nav Links (Left side of layout - slicing first 3 links) */}
                        <div className="hidden lg:flex items-center space-x-6"> 
                            {navLinks.slice(0, 3).map(item => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`desktop-nav-link text-sm font-medium uppercase tracking-wider ${location.pathname === item.to ? 'active' : ''}`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                    
                    {/* RIGHT GROUP */}
                    <div className="flex items-center space-x-2 lg:space-x-6 ml-auto"> 
                        
                        {/* Desktop Nav Links (Right side of layout - slicing the rest) */}
                        <div className="hidden lg:flex items-center space-x-6"> 
                            {navLinks.slice(3).map(item => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`desktop-nav-link text-sm font-medium uppercase tracking-wider ${location.pathname === item.to ? 'active' : ''}`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>

                        <button 
                            onClick={openModal} 
                            className="btn-brand-red whitespace-nowrap" 
                        >
                            Get A Quote
                        </button>

                        <button 
                            onClick={() => setIsMenuOpen(true)} 
                            className="lg:hidden flex items-center justify-center p-2 rounded-full text-brand-dark hover:text-brand-red transition duration-200 focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2"
                            aria-label="Open mobile menu"
                        >
                            <MenuIcon size={24} strokeWidth={2}/> 
                        </button>
                    </div>
                </div>
            </header>
            
            <MobileMenu 
                isOpen={isMenuOpen} 
                closeMenu={closeMenu}
                location={location}
            />

            <ContactModal
                isOpen={isModalOpen}
                onClose={closeModal}
            />
        </>
    );
};

export default DesignNavbar;